'use client';

import { decideMathCommit } from '@magicschool/math';
import type { MathfieldElement } from 'mathlive';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

export interface UseMathFieldCommitOptions {
  /** The expression the document holds. */
  readonly expression: string;
  /** Whether the field is open; each opening starts an edit session with nothing typed. */
  readonly isEditing: boolean;
  /** Writes an expression back to the document. */
  readonly onSave: (expression: string) => void;
  /** Removes the math when an edit ends empty; without it, a cleared expression is saved as `''`. */
  readonly onRemove?: () => void;
}

export interface MathFieldProps {
  readonly ref: RefObject<MathfieldElement | null>;
  readonly onValueChange: () => void;
}

export interface MathFieldCommit {
  /** Spread onto `EditableMath`: the field the commit reads, and the edit tracking. */
  readonly fieldProps: MathFieldProps;
  /** Ends the edit session, writing only what the teacher changed. */
  readonly commit: () => void;
}

/**
 * The close-the-field half of editing math in place. Closing a field the teacher did not type in
 * never overwrites a stored expression, whatever the field reads: MathLive can blank or re-serialize
 * LaTeX it cannot parse.
 */
export function useMathFieldCommit({ expression, isEditing, onSave, onRemove }: UseMathFieldCommitOptions): MathFieldCommit {
  const ref = useRef<MathfieldElement | null>(null);
  const wasEdited = useRef(false);

  useEffect(() => {
    if (isEditing) wasEdited.current = false;
  }, [isEditing]);

  const onValueChange = useCallback(() => {
    wasEdited.current = true;
  }, []);

  const commit = useCallback(() => {
    const field = ref.current;
    if (!field) return;
    const decision = decideMathCommit({ stored: expression, draft: field.value ?? '', edited: wasEdited.current });
    switch (decision.kind) {
      case 'keep':
        return;
      case 'discard':
        discardExpression(expression, onSave, onRemove);
        return;
      case 'save':
        onSave(decision.expression);
    }
  }, [expression, onSave, onRemove]);

  const fieldProps = useMemo(() => ({ ref, onValueChange }), [onValueChange]);
  return { fieldProps, commit };
}

function discardExpression(stored: string, onSave: (expression: string) => void, onRemove?: () => void): void {
  if (onRemove) {
    onRemove();
    return;
  }
  const hasStoredExpression = stored !== '';
  if (hasStoredExpression) onSave('');
}
